/**
 * Panneau « Votre réservation » de la fiche v2 (maquette `.bookpanel`, SPEC FC1, FC6, FC7).
 * Briques de présentation seulement : la logique vit dans ClassScreen / useBooking.
 */
import type { ReactNode } from 'react';
import type { ClientService } from 'types/client';
import { dayMonthYear } from '../lib/format';
import type { SessionInfo } from '../lib/session';
import { Icon, type IconName } from './Icon';


/** Séance (R1) : date, horaire, studio + adresse, coach, sortis du bandeau. */
export function SessionBlock({ session }: { session: SessionInfo }) {
  const rows: Array<{ icon: IconName; label: string; main: ReactNode; sub?: string }> = [
    { icon: 'cal', label: 'Date', main: session.date },
    { icon: 'clock', label: 'Horaire', main: session.time },
    { icon: 'pin', label: 'Studio', main: session.studio, sub: session.address },
    { icon: 'user', label: 'Coach', main: session.coach },
  ];
  return (
    <dl className="mmv2-session">
      {rows
        .filter((row) => row.main)
        .map((row) => (
          <div className="mmv2-session__row" key={row.label}>
            <dt>
              <Icon name={row.icon} size={16} />
              <span className="mmv2-visually-hidden">{row.label}</span>
            </dt>
            <dd>
              <b>{row.main}</b>
              {row.sub ? <span>{row.sub}</span> : null}
            </dd>
          </div>
        ))}
    </dl>
  );
}

export function Line({ label, children, total }: { label: string; children: ReactNode; total?: boolean }) {
  return (
    <div className={`mmv2-bp-line${total ? ' mmv2-bp-line--total' : ''}`}>
      <span>{label}</span>
      <b>{children}</b>
    </div>
  );
}

export function SummaryCard({
  kicker,
  booked,
  session,
  children,
}: {
  kicker: string;
  booked?: boolean;
  session?: SessionInfo | null;
  children?: ReactNode;
}) {
  return (
    <section className={`mmv2-card mmv2-bp-sect mmv2-class-summary${booked ? ' mmv2-class-summary--booked' : ''}`} aria-label={kicker}>
      {booked ? (
        <h2 className="mmv2-class-summary__booked">
          <Icon name="check" size={20} stroke={2.4} />
          {kicker}
        </h2>
      ) : (
        <h2 className="mmv2-kicker mmv2-class-summary__kicker">{kicker}</h2>
      )}
      {session ? <SessionBlock session={session} /> : null}
      {children ? <div className="mmv2-class-summary__body">{children}</div> : null}
    </section>
  );
}

/*
 * Option courte (« Pack découverte · 1 restant ») : la liste fermée, large de 300 px au bureau, coupait la date
 * d'expiration, critère de présélection (revue du 04/10). La date du forfait choisi passe sous la liste.
 */
const serviceOption = (service: ClientService, needed: number) => {
  const remaining = service.Remaining ?? 0;
  return `${service.Name} · ${remaining}\u00a0restant${remaining > 1 ? 's' : ''}${remaining < needed ? ' · insuffisant' : ''}`;
};

/** Choix du forfait dans le panneau (SPEC FC6) : liste si plusieurs, phrase si un seul. */
export function ServicePicker({
  services,
  selectedId,
  needed,
  onChange,
}: {
  services: ClientService[];
  selectedId?: number;
  needed: number;
  onChange: (id: number) => void;
}) {
  if (!services.length) return null;
  const selected = services.find((s) => Number(s.Id) === selectedId) ?? services[0];
  if (services.length === 1) {
    return (
      <p className="mmv2-tiny mmv2-muted mmv2-bp-pack">
        Débités du pack «&nbsp;{selected.Name}&nbsp;»
        {selected.ExpirationDate ? `, qui expire le ${dayMonthYear(new Date(selected.ExpirationDate))}` : ''}.
      </p>
    );
  }
  return (
    <div className="mmv2-field mmv2-bp-service">
      <label className="mmv2-label" htmlFor="mmv2-class-service">
        Crédits à utiliser
      </label>
      <select
        id="mmv2-class-service"
        className="mmv2-input mmv2-select"
        value={selectedId ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {services.map((service) => (
          <option key={service.Id} value={service.Id} disabled={(service.Remaining ?? 0) < needed}>
            {serviceOption(service, needed)}
          </option>
        ))}
      </select>
      {selected.ExpirationDate ? (
        <p className="mmv2-tiny mmv2-muted mmv2-bp-expiry">Expire le {dayMonthYear(new Date(selected.ExpirationDate))}.</p>
      ) : null}
    </div>
  );
}

export function Notice({
  tone = 'info',
  icon,
  children,
  role,
  focusAlert,
}: {
  tone?: 'info' | 'warm' | 'green';
  icon?: IconName;
  children: ReactNode;
  role?: 'alert' | 'status';
  /** Encadré d'échec : ClassScreen l'amène à l'écran et lui donne le focus quand il apparaît (tabIndex -1). */
  focusAlert?: boolean;
}) {
  return (
    <div
      className={`mmv2-notice mmv2-class-notice${tone === 'warm' ? ' mmv2-notice--warm' : ''}${tone === 'green' ? ' mmv2-class-notice--green' : ''}`}
      role={role}
      tabIndex={focusAlert ? -1 : undefined}
      data-focus-alert={focusAlert ? '' : undefined}
    >
      <Icon name={icon ?? (tone === 'warm' ? 'alert' : tone === 'green' ? 'check' : 'info')} />
      <div>{children}</div>
    </div>
  );
}

/** Encadré « Venez à deux » (SPEC FC7, R5) : interrupteur, aide explicite, spot de l'invité. Sans prénom (lot E). */
export function GuestBox({
  on,
  onToggle,
  guestSpot,
  creditsRequired,
  disabledReason,
  onBuyCredits,
}: {
  on: boolean;
  onToggle: () => void;
  guestSpot: number | null;
  creditsRequired: number;
  disabledReason?: string | null;
  /** Interrupteur désactivé faute de crédits : lien vers les tarifs (retours de Daphné du 14/10). */
  onBuyCredits?: () => void;
}) {
  const disabled = !!disabledReason;
  return (
    <section className="mmv2-card mmv2-bp-sect" aria-label="Venez à deux">
      <div className={`mmv2-guestbox${on ? ' is-on' : ''}`}>
        <button
          type="button"
          className="mmv2-guestbox__switch"
          aria-pressed={on}
          onClick={onToggle}
          disabled={disabled}
          aria-describedby="mmv2-guest-help"
        >
          <i aria-hidden="true" />
          <span className="mmv2-guestbox__label">Venez à deux</span>
        </button>
        <p className="mmv2-guestbox__help" id="mmv2-guest-help">
          {disabled ? (
            disabledReason
          ) : on ? (
            <>
              Choisissez aussi le spot de votre invité sur le plan, après le vôtre. Une 2<sup>e</sup> place est débitée de vos
              crédits ({creditsRequired > 1 ? `${creditsRequired}\u00a0crédits` : '1\u00a0crédit'} de plus). Votre invité n’a pas besoin de
              compte.
            </>
          ) : (
            <>Réservez un second spot sur vos crédits. Votre invité n’a pas besoin de compte.</>
          )}
        </p>
        {disabled && onBuyCredits ? (
          <p className="mmv2-guestbox__buy">
            <button type="button" className="mmv2-linkish" onClick={onBuyCredits}>
              Acheter des crédits
            </button>
          </p>
        ) : null}
        {on ? (
          // Libellé et valeur dans un seul élément à côté de la pastille : le nœud texte, élément flex à part entière, se
          // cassait en deux colonnes (« Spot de / l’invité : ») à 1440 (revue du 04/10).
          <p className="mmv2-guestbox__status" aria-live="polite">
            <i className="mmv2-legend-swatch" data-state="guest" aria-hidden="true" />
            <span>
              Spot de l’invité&nbsp;: <b>{guestSpot !== null ? `#${guestSpot}` : 'À choisir sur le plan'}</b>
            </span>
          </p>
        ) : null}
      </div>
    </section>
  );
}
